import {ReactNode} from "react";

import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import LocationOn from "@mui/icons-material/LocationOn";

import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import History from "@mui/icons-material/History";

import SensorsOutlined from "@mui/icons-material/SensorsOutlined";
import Sensors from "@mui/icons-material/Sensors";

import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import Summarize from "@mui/icons-material/Summarize";

import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import LocalShipping from "@mui/icons-material/LocalShipping";

import BuildCircleOutlined from "@mui/icons-material/BuildCircleOutlined";
import BuildCircle from "@mui/icons-material/BuildCircle";

import GroupOutlined from "@mui/icons-material/GroupOutlined";
import Group from "@mui/icons-material/Group";

import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import Speed from "@mui/icons-material/Speed";

import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import ReceiptLong from "@mui/icons-material/ReceiptLong";

import SettingsRemoteOutlined from "@mui/icons-material/SettingsRemoteOutlined";
import SettingsRemote from "@mui/icons-material/SettingsRemote";

import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import Business from "@mui/icons-material/Business";

import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";
import CreditCard from "@mui/icons-material/CreditCard";

import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";
import MonitorHeart from "@mui/icons-material/MonitorHeart";

import BusAlertOutlined from "@mui/icons-material/BusAlertOutlined";
import BusAlert from "@mui/icons-material/BusAlert";

import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import MenuBook from "@mui/icons-material/MenuBook";

import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import Settings from "@mui/icons-material/Settings";
import {routes} from "../utils/routes";

import DashboardIcon from "@mui/icons-material/Dashboard";

import AltRouteOutlined from "@mui/icons-material/AltRouteOutlined";
import AltRoute from "@mui/icons-material/AltRoute";
import {DIRECTOR_ROLE_ID, DISPATCHER_ROLE_ID, DRIVER_ROLE_ID} from "../models/IUser";

/**
 * ============================================================================
 * menuConfig — структура бокового меню.
 * ============================================================================
 *
 * НЕ ПУТАТЬ с routesConfig:
 *   - routesConfig (config/routes.tsx) — карта URL → компонент + защита роута.
 *     Источник истины для React Router. Не группирует.
 *   - menuConfig (этот файл) — порядок и группировка ВИДИМОГО меню.
 *     Источник истины для сайдбара. Не знает про lazy/Suspense.
 *
 * Разделение даёт:
 *   - Возможность переставлять пункты меню без правки роутера.
 *   - Возможность вообще не показывать роут в меню (просто не добавлять сюда).
 *   - Чистый routesConfig без UI-полей (icon/iconActive/group).
 *
 * Иконки:
 *   icon       — outlined, для неактивного состояния.
 *   iconActive — filled, для активного состояния.
 *                Если iconActive не задан, используется icon (без переключения).
 *
 * Видимость:
 *   requiresAdmin   — пункт виден только админам.
 *   requiresTariff  — пункт виден только на указанном тарифе (например, "pro").
 *
 *   Эти же поля могут быть у ГРУППЫ — тогда вся группа скрывается.
 *   Если у группы есть условие И у пунктов внутри — оба должны выполниться.
 * ============================================================================
 */

export interface MenuItemConfig {
    path: string;
    label: string;
    icon: ReactNode;
    iconActive?: ReactNode;
    requiresTariff?: string;
    requiresAdmin?: boolean;
    /** Пункт виден только ролям из списка (role_id, см. models/IUser). */
    requiresRoles?: number[];
}

export interface MenuGroupConfig {
    /** Стабильный id группы — используется как ключ в localStorage для collapsible. */
    id: string;
    /** Заголовок группы (caption uppercase). Если undefined — группа без заголовка. */
    title?: string;
    items: MenuItemConfig[];
    /** Если true — группа сворачиваемая (с ExpandMore триггером). */
    collapsible?: boolean;
    /** Начальное состояние раскрытия (если нет сохранённого в localStorage). */
    defaultOpen?: boolean;
    /** Скрыть всю группу для не-админов. */
    requiresAdmin?: boolean;
    /** Скрыть всю группу для ролей вне списка. */
    requiresRoles?: number[];
    /** Скрыть всю группу для не подходящего тарифа. */
    requiresTariff?: string;
    /**
     * Прижать группу к низу sidebar'a. Используется для редко-используемых
     * утилитарных разделов (справочники, документация).
     * Если true — группа рендерится отдельно, в нижней зоне drawer'а.
     */
    pinToBottom?: boolean;
    /**
     * Иконка для триггера сворачиваемой группы (collapsible=true).
     * Для несворачиваемых групп игнорируется.
     */
    icon?: ReactNode;
    iconActive?: ReactNode;
}

export const menuConfig: MenuGroupConfig[] = [
    // ─── 1. Оперативная работа (без заголовка) ──────────────────────────────
    {
        id: "operations",
        items: [
            {
                path: routes.fleetDashboard,
                label: "Сводка",
                icon: <DashboardIcon/>,
                iconActive: <DashboardIcon/>,
                requiresTariff: "pro",
            },
            {
                // Временно вернули на старую Leaflet-карту (у GL-версии просела
                // производительность — ~34 fps против 60, разбираемся). GL-версия
                // доступна напрямую по routes.mapGl.
                path: routes.mapGl,
                label: "Карта",
                icon: <LocationOnOutlined/>,
                iconActive: <LocationOn/>,
            },
            {
                // Временно вернули на старую Leaflet-историю (разбираемся с
                // производительностью GL). GL-версия доступна по routes.mapHistoryGl.
                path: routes.mapHistoryGl,
                label: "История",
                icon: <HistoryOutlined/>,
                iconActive: <History/>,
            },
            {
                path: routes.machinery_telemetry,
                label: "Телеметрия",
                icon: <SensorsOutlined/>,
                iconActive: <Sensors/>,
                requiresTariff: "pro",
            },
            {
                // Отчёты по группам техники. Виден всем ролям: по этому
                // отчёту считают аренду, и доступ к нему нужен не только
                // руководителю (управлять группами он по-прежнему не даёт —
                // это в диалоге на «Технике»).
                path: routes.groupReports,
                label: "Отчёты",
                icon: <SummarizeOutlined/>,
                iconActive: <Summarize/>,
            },
            /*{
                path: routes.dispatch,
                label: "Диспетчеризация",
                icon: <AltRouteOutlined/>,
                iconActive: <AltRoute/>,
                requiresRoles: [DIRECTOR_ROLE_ID, DISPATCHER_ROLE_ID],
            },
            {
                path: routes.driverDay,
                label: "Мой день",
                icon: <AltRouteOutlined/>,
                iconActive: <AltRoute/>,
                requiresRoles: [DRIVER_ROLE_ID],
            },*/
        ],
    },

    // ─── 2. Управление парком (без заголовка) ───────────────────────────────
    {
        id: "fleet",
        items: [
            {
                path: routes.machinery,
                label: "Список техники",
                icon: <LocalShippingOutlined/>,
                iconActive: <LocalShipping/>,
            },
            {
                path: routes.machineryMaintenance,
                label: "Тех. обсл.",
                icon: <BuildCircleOutlined/>,
                iconActive: <BuildCircle/>,
            },
            {
                path: routes.users,
                label: "Сотрудники",
                icon: <GroupOutlined/>,
                iconActive: <Group/>,
            },
            {
                path: routes.driving_events,
                label: "Стиль вождения",
                icon: <SpeedOutlined/>,
                iconActive: <Speed/>,
            },
        ],
    },

    // ─── 3. Админ (сворачиваемая группа) ────────────────────────────────────
    {
        id: "admin",
        title: "Админ",
        collapsible: true,
        defaultOpen: false,
        requiresAdmin: true,
        icon: <SettingsOutlined/>,
        iconActive: <Settings/>,
        items: [
            {
                path: routes.clientInvoices,
                label: "Счета",
                icon: <ReceiptLongOutlined/>,
                iconActive: <ReceiptLong/>,
            },
            {
                path: routes.trackers,
                label: "Трекеры",
                icon: <SettingsRemoteOutlined/>,
                iconActive: <SettingsRemote/>,
            },
            {
                path: routes.clients,
                label: "Клиенты",
                icon: <BusinessOutlined/>,
                iconActive: <Business/>,
            },
            {
                path: routes.tariffs,
                label: "Тарифы",
                icon: <CreditCardOutlined/>,
                iconActive: <CreditCard/>,
            },
            {
                path: routes.ml_monitor,
                label: "ML монитор",
                icon: <MonitorHeartOutlined/>,
                iconActive: <MonitorHeart/>,
            },
            {
                path: routes.backup,
                label: "Бекапы",
                icon: <MonitorHeartOutlined/>,
                iconActive: <MonitorHeart/>,
            },
            {
                path: routes.machinery_admin,
                label: "Техника",
                icon: <MonitorHeartOutlined/>,
                iconActive: <MonitorHeart/>,
            },
            {
                path: routes.machinery_admin_map,
                label: "Карта техники",
                icon: <LocationOnOutlined/>,
                iconActive: <LocationOn/>,
            },
        ],
    },

    // ─── 4. Справочники (внизу sidebar'а, с заголовком) ─────────────────────
    {
        id: "references",
        title: "Справочники",
        pinToBottom: true,
        items: [
            {
                path: routes.errorCode,
                label: "Коды ошибок",
                icon: <BusAlertOutlined/>,
                iconActive: <BusAlert/>,
            },
            {
                path: routes.technicalLiterature,
                label: "Тех. литература",
                icon: <MenuBookOutlined/>,
                iconActive: <MenuBook/>,
            },
        ],
    },
];